import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import { formConsumer, Field, CheckboxGroup, InputGroup, Switch, Select } from 'core/form';
import { Flex, Text, Link, Callout, Heading } from 'core/components';
import {
  CredentialOptionRenderer,
  CredentialValueRenderer,
  KentikAgentOptionRenderer
} from 'app/views/setup/tasks/nms/optionRenderers';
import SetupCredentials from 'app/views/setup/tasks/nms/SetupCredentials';
import { GROUP_ID_PREFIX } from '@kentik/ui-shared/agents/constants';
import { isIpValid } from 'core/util/ip';
import { TestConnection } from 'app/views/settings/devices/form/TestConnection';
import { collectionMethodRenderer } from './IpSnmpSettings';

@formConsumer
@inject('$auth', '$devices', '$dictionary', '$credentials', '$kentikAgents', '$discovery')
@observer
class StSettings extends Component {
  state = {
    isAddingCredentialDialogOpen: false,
    credentialModel: null,
    isGnmiTestingConnection: false,
    gnmiTestConnectionResult: null
  };

  constructor(props) {
    super(props);
    const { form, $auth } = this.props;

    // Hide the kentik_agent option if user doesn't have recon.enabled permission
    if (!$auth.hasPermission('recon.enabled', { overrideForSudo: false })) {
      const collectionField = form.getField('streaming_telemetry_collection_method');
      collectionField.options = collectionField.options.filter((option) => option.value !== 'kentik_agent');
      form.validate();
    }
  }

  componentDidMount() {
    const { form } = this.props;

    // Streaming Telemetry collection via kProxy
    if (form.getValue('device_gnmi_v1_conf')) {
      form.setValue('streaming_telemetry_collection_method', ['kproxy']);
    }
  }

  handleCredentialChange = (field, value) => {
    const { $credentials } = this.props;
    // open the new credential dialog
    if (value === '$NEW') {
      field.setValue('');
      this.setState({
        isAddingCredentialDialogOpen: true,
        credentialModel: $credentials.groupedCollection.forge({ type: 'streaming_telemetry' })
      });
    }
  };

  handleCloseCredentialsDialog = (formValues) => {
    const { form } = this.props;

    if (formValues) {
      const { credentialName } = formValues;
      form.setValue('streaming_telemetry_collection_options.credential_name', credentialName);
    }

    this.setState({ isAddingCredentialDialogOpen: false, credentialModel: null });
  };

  get gnmiTestConnectionParams() {
    const { form } = this.props;
    const port = form.getValue('streaming_telemetry_collection_options.port');
    const timeout = form.getValue('streaming_telemetry_collection_options.timeout');
    return {
      agentId: form.getValue('kentik_agent_snmp_options.agent_id'),
      gnmiIp: form.getValue('kentik_agent_snmp_options.ranger_snmp_ip')?.trim(),
      credentialName: form.getValue('streaming_telemetry_collection_options.credential_name'),
      port: Number(port == null || port === '' ? 6030 : port),
      timeout: Number(timeout == null || timeout === '' ? 2 : timeout),
      useSsl: !!form.getValue('streaming_telemetry_collection_options.use_ssl')
    };
  }

  get gnmiTestConnectionErrors() {
    const { form } = this.props;
    const errors = [];
    const agentId = form.getValue('kentik_agent_snmp_options.agent_id');
    const gnmiIp = form.getValue('kentik_agent_snmp_options.ranger_snmp_ip')?.trim();

    if (!agentId) {
      errors.push('Collection Agent is required. Select an agent on the SNMP tab.');
    } else if (agentId.includes(GROUP_ID_PREFIX)) {
      errors.push('Select an individual Collection Agent on the SNMP tab. Agent groups are not supported.');
    } else {
      errors.push(...(form.getField('kentik_agent_snmp_options.agent_id')?.errors || []));
    }

    if (!gnmiIp) {
      errors.push('Device IP is required. Enter an IP address on the SNMP tab.');
    } else if (!isIpValid(gnmiIp)) {
      errors.push('Device IP must be a valid IP address. Update it on the SNMP tab.');
    } else {
      errors.push(...(form.getField('kentik_agent_snmp_options.ranger_snmp_ip')?.errors || []));
    }

    errors.push(...(form.getField('streaming_telemetry_collection_options.port')?.errors || []));
    errors.push(...(form.getField('streaming_telemetry_collection_options.timeout')?.errors || []));

    return [...new Set(errors)];
  }

  get canGnmiTestConnection() {
    const { isGnmiTestingConnection } = this.state;
    return !isGnmiTestingConnection && this.gnmiTestConnectionErrors.length === 0;
  }

  handleGnmiTestConnection = async () => {
    const { $discovery } = this.props;
    const parameters = this.gnmiTestConnectionParams;
    this.setState({ isGnmiTestingConnection: true, gnmiTestConnectionResult: null });

    return $discovery
      .rangerGnmiTestConnection(parameters)
      .then((response) => {
        const isSuccess = !!response && response.success !== false && !response.error;
        this.setState({
          gnmiTestConnectionResult: {
            success: isSuccess,
            data: response,
            connectionSuccessful: isSuccess,
            error: isSuccess ? null : response?.error || response?.message || 'Connection failed',
            timestamp: Date.now(),
            parameters
          }
        });
      })
      .catch((error) => {
        this.setState({
          gnmiTestConnectionResult: {
            success: false,
            connectionSuccessful: false,
            error: error.message || 'Test connection failed. Please check your configuration and try again.',
            timestamp: Date.now(),
            parameters
          }
        });
      })
      .finally(() => this.setState({ isGnmiTestingConnection: false }));
  };

  render() {
    const { form, $dictionary, $devices, $credentials, $kentikAgents, role } = this.props;
    const { isAddingCredentialDialogOpen, credentialModel, isGnmiTestingConnection, gnmiTestConnectionResult } =
      this.state;
    const { companySettings } = $devices;
    const { sending_ips } = form.getValues();
    const kbLinkST = $dictionary.getHelpUrl('admin', 'configureST');

    const useSSL = form.getValue('streaming_telemetry_collection_options.use_ssl');
    // const useCertificateAuth = form.getValue('streaming_telemetry_collection_options.enable_certificate_auth');
    const useKProxyCollection = form.getValue('streaming_telemetry_collection_method').includes('kproxy');
    const useKentikAgentCollection = form.getValue('streaming_telemetry_collection_method').includes('kentik_agent');

    const credentialTypes = ['streaming_telemetry'];
    const credentialOptions = $credentials.getCredentialOptionsByTypes(credentialTypes);

    const ariaProps = {};
    if (role) {
      ariaProps.role = role;
    }

    return (
      <Flex flexDirection="column" overflow="auto" gap={2} p={2} {...ariaProps}>
        <Heading level={5} pb="6px" mb={0} borderBottom="thin">
          Collection Method
        </Heading>

        <Field m={0} name="streaming_telemetry_collection_method" showLabel={false}>
          <CheckboxGroup
            containerProps={{ flexDirection: 'column', gap: '8px' }}
            itemRenderer={collectionMethodRenderer}
          />
        </Field>

        {useKentikAgentCollection && (
          <Flex flexDirection="column" gap={2}>
            <Heading level={5} pb="6px" mb={0} borderBottom="thin">
              Dial-In from Universal Agent Options
            </Heading>

            <Flex gap={2}>
              <Field
                m={0}
                flex={1}
                onChange={this.handleCredentialChange}
                name="streaming_telemetry_collection_options.credential_name"
                options={[
                  { value: '$NEW', label: 'New Credential', icon: 'plus', intent: 'primary' },
                  ...credentialOptions
                ]}
              >
                <Select fill optionRenderer={CredentialOptionRenderer} valueRenderer={CredentialValueRenderer} />
              </Field>

              <Flex flexDirection="column" gap={1} flex={1}>
                <Field
                  m={0}
                  flex={1}
                  name="kentik_agent_snmp_options.agent_id"
                  options={$kentikAgents.nmsAssignmentOptions}
                  disabled
                >
                  <Select fill optionRenderer={KentikAgentOptionRenderer} />
                </Field>
                <Text muted>Streaming Telemetry collection is handled by the agent configured on the SNMP tab.</Text>
              </Flex>
            </Flex>

            <Flex gap={2}>
              <Field m={0} name="streaming_telemetry_collection_options.timeout">
                <InputGroup width={150} />
              </Field>

              <Field m={0} name="streaming_telemetry_collection_options.port">
                <InputGroup width={150} />
              </Field>
            </Flex>

            <Flex flexDirection="column" alignItems="flex-start" gap={1}>
              <Field m={0} name="streaming_telemetry_collection_options.use_ssl" showLabel={false}>
                <Switch switchLabel="Use secure connection (TLS)" />
              </Field>
              {useSSL && (
                <Callout intent="warning">
                  Certificate verification is not currently supported for connection tests. TLS encryption is enabled,
                  but the server identity is not verified.
                </Callout>
              )}
              <TestConnection
                canTestConnection={this.canGnmiTestConnection}
                testConnectionErrors={this.gnmiTestConnectionErrors}
                handleTestConnection={this.handleGnmiTestConnection}
                isTestingConnection={isGnmiTestingConnection}
                testConnectionResult={
                  gnmiTestConnectionResult && {
                    ...gnmiTestConnectionResult,
                    parameters: {
                      ...gnmiTestConnectionResult.parameters,
                      rangerSnmpIp: gnmiTestConnectionResult.parameters.gnmiIp
                    }
                  }
                }
              />
            </Flex>
          </Flex>
        )}

        {useKProxyCollection && (
          <Callout intent="primary" title="Dial-Out to kProxy Configuration">
            <Flex flexDirection="column" gap="12px">
              <Flex>
                <Text as="div" pr={1}>
                  Send streaming telemetry from IP address:
                </Text>
                {sending_ips && sending_ips?.length > 0 ? (
                  <Text as="div" fontWeight="bold" monospace>
                    {sending_ips[0]}
                  </Text>
                ) : (
                  <Text as="div" muted>
                    {'<< add Sending IP on Flow tab >>'}
                  </Text>
                )}
              </Flex>
              <Text as="div">Send streaming telemetry to Kentik at the following destination:</Text>
              <Flex gap={1} ml={2}>
                <Text as="div" width={130}>
                  Kentik Ingest IP:
                </Text>
                <Text as="div" fontWeight="bold" monospace>
                  {companySettings.flow_ips?.join(', ')}
                </Text>
              </Flex>
              <Flex gap={1} ml={2}>
                <Text as="div" width={130}>
                  Kentik ST port:
                </Text>
                <Text as="div" fontWeight="bold" monospace>
                  {20023}
                </Text>
              </Flex>

              <Link to={kbLinkST} blank>
                Need help configuring Streaming Telemetry?
              </Link>
            </Flex>
          </Callout>
        )}

        {credentialModel && (
          <SetupCredentials
            isOpen={isAddingCredentialDialogOpen}
            model={credentialModel}
            onClose={this.handleCloseCredentialsDialog}
            types={credentialTypes}
            title="Add Streaming Telemetry Credential"
            // since we are adding a Streaming Telemetry credential here, type selection is not necessary
            hideTypeSelector
          />
        )}
      </Flex>
    );
  }
}

export default StSettings;
